import apiClient from './api'
import type { PlatformUser, Tenant, CreateTenantRequest, TenantUser } from '@/types/api.types'

/**
 * Cliente de la capa de PLATAFORMA (admin de sistema). El JWT (scope=platform) viaja
 * en la cookie HttpOnly `accessToken` que setea el backend; aquí solo guardamos el
 * objeto de usuario para el estado local. Una sesión a la vez (comparte cookie con tenant).
 */
export const platformService = {
    async login(username: string, password: string, mfaCode?: string): Promise<PlatformUser> {
        try {
            const res = await apiClient.post<{ user: PlatformUser }>(
                '/api/v1/platform/auth/login', { username, password, mfaCode })
            localStorage.setItem('platformUser', JSON.stringify(res.data.user))
            // Una sesión a la vez: limpiar cualquier sesión de tenant local
            localStorage.removeItem('user')
            return res.data.user
        } catch (err: any) {
            if (err?.response?.status === 401 && err?.response?.data?.mfaRequired) {
                throw { mfaRequired: true }
            }
            throw err
        }
    },

    // ---- MFA / TOTP ----
    async mfaSetup(): Promise<{ secret: string; otpauthUri: string }> {
        return (await apiClient.post('/api/v1/platform/auth/mfa/setup')).data
    },
    async mfaEnable(code: string): Promise<void> {
        await apiClient.post('/api/v1/platform/auth/mfa/enable', { code })
    },
    async mfaStatus(): Promise<{ mfaEnabled: boolean }> {
        return (await apiClient.get('/api/v1/platform/auth/mfa/status')).data
    },

    async me(): Promise<PlatformUser> {
        const res = await apiClient.get<PlatformUser>('/api/v1/platform/auth/me')
        localStorage.setItem('platformUser', JSON.stringify(res.data))
        return res.data
    },

    async logout(): Promise<void> {
        try { await apiClient.post('/api/v1/platform/auth/logout') }
        finally { localStorage.removeItem('platformUser') }
    },

    isAuthenticated(): boolean {
        return !!localStorage.getItem('platformUser')
    },

    // ---- Tenants ----
    async listTenants(): Promise<Tenant[]> {
        return (await apiClient.get<Tenant[]>('/api/v1/platform/tenants')).data
    },
    async createTenant(req: CreateTenantRequest): Promise<Tenant> {
        return (await apiClient.post<Tenant>('/api/v1/platform/tenants', req)).data
    },
    async activateTenant(id: string): Promise<Tenant> {
        return (await apiClient.patch<Tenant>(`/api/v1/platform/tenants/${id}/activate`)).data
    },
    async suspendTenant(id: string): Promise<Tenant> {
        return (await apiClient.patch<Tenant>(`/api/v1/platform/tenants/${id}/suspend`)).data
    },
    async listTenantUsers(slug: string): Promise<TenantUser[]> {
        return (await apiClient.get<TenantUser[]>(`/api/v1/platform/tenants/${slug}/users`)).data
    },
    async createTenantUser(slug: string, body: { username: string; fullName?: string; email?: string; password: string; role: string }): Promise<TenantUser> {
        return (await apiClient.post<TenantUser>(`/api/v1/platform/tenants/${slug}/users`, body)).data
    },
    async setTenantUserActive(slug: string, id: string, active: boolean): Promise<void> {
        await apiClient.patch(`/api/v1/platform/tenants/${slug}/users/${id}/active`, { active })
    },
    async resetTenantUserPassword(slug: string, id: string, password: string): Promise<void> {
        await apiClient.post(`/api/v1/platform/tenants/${slug}/users/${id}/reset-password`, { password })
    },

    // ---- Salud de servicios ----
    async healthServices(): Promise<{ name: string; endpoint: string; up: boolean; latencyMs: number }[]> {
        return (await apiClient.get('/api/v1/platform/health/services')).data
    },

    // ---- Auditoría y seguridad ----
    async auditEvents(): Promise<{ chainValid: boolean; events: { id: number; ts: string; actor: string; action: string; target: string; ip: string }[] }> {
        return (await apiClient.get('/api/v1/platform/audit/events')).data
    },
    async securitySummary(): Promise<{ loginSuccess: number; loginFailure: number; mfaEnabledEvents: number; chainValid: boolean; recentFailures: { ts: string; target: string; ip: string }[] }> {
        return (await apiClient.get('/api/v1/platform/security/summary')).data
    },

    // ---- Finanzas (solo lectura; emisión real desactivada por flags) ----
    async billingStatus(): Promise<{ enabled: boolean; invoiceGeneration: boolean; flow: boolean; libreDTE: boolean }> {
        return (await apiClient.get('/api/v1/platform/billing/status')).data
    },
    async billingMetrics(): Promise<any> { return (await apiClient.get('/api/v1/platform/billing/metrics')).data },
    async billingPlans(): Promise<any[]> { return (await apiClient.get('/api/v1/platform/billing/plans')).data },
    async billingSubscriptions(): Promise<any[]> { return (await apiClient.get('/api/v1/platform/billing/subscriptions')).data },
    async billingInvoices(): Promise<any[]> { return (await apiClient.get('/api/v1/platform/billing/invoices')).data },
    async billingPayments(): Promise<any[]> { return (await apiClient.get('/api/v1/platform/billing/payments')).data },
    async billingManualCharge(body: any): Promise<any> { return (await apiClient.post('/api/v1/platform/billing/manual-charge', body)).data },
    async billingUploadReceipt(file: File): Promise<{ key: string }> {
        const fd = new FormData(); fd.append('file', file)
        return (await apiClient.post('/api/v1/platform/billing/receipt-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data
    },
    async billingReceiptBlob(key: string): Promise<Blob> {
        return (await apiClient.get('/api/v1/platform/billing/receipt', { params: { key }, responseType: 'blob' })).data
    },

    // ---- CRM · Leads ----
    async crmLeads(): Promise<any[]> { return (await apiClient.get('/api/v1/platform/crm/leads')).data },
    async crmCreateLead(body: any): Promise<any> { return (await apiClient.post('/api/v1/platform/crm/leads', body)).data },
    async crmUpdateLead(id: string, body: any): Promise<any> { return (await apiClient.patch(`/api/v1/platform/crm/leads/${id}`, body)).data },
    async crmDeleteLead(id: string): Promise<void> { await apiClient.delete(`/api/v1/platform/crm/leads/${id}`) },

    // ---- CRM · Cuentas ----
    async crmAccounts(): Promise<any[]> { return (await apiClient.get('/api/v1/platform/crm/accounts')).data },
    async crmCreateAccount(body: any): Promise<any> { return (await apiClient.post('/api/v1/platform/crm/accounts', body)).data },
    async crmDeleteAccount(id: string): Promise<void> { await apiClient.delete(`/api/v1/platform/crm/accounts/${id}`) },

    // ---- CRM · Oportunidades (pipeline) ----
    async crmOpportunities(): Promise<any[]> { return (await apiClient.get('/api/v1/platform/crm/opportunities')).data },
    async crmCreateOpp(body: any): Promise<any> { return (await apiClient.post('/api/v1/platform/crm/opportunities', body)).data },
    async crmUpdateOpp(id: string, body: any): Promise<any> { return (await apiClient.patch(`/api/v1/platform/crm/opportunities/${id}`, body)).data },
    async crmDeleteOpp(id: string): Promise<void> { await apiClient.delete(`/api/v1/platform/crm/opportunities/${id}`) },

    // ---- CRM · BI ----
    async crmBi(): Promise<any> { return (await apiClient.get('/api/v1/platform/crm/bi/summary')).data },
}
